import React, { useState, useMemo, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {
  getCPFFormatado,
  getCNPJFormatado,
  validarCPF,
  validarCNPJ,
  getMascaraMoeda,
  getFloat,
} from 'utils/uteis';
import { formatCurrency } from 'utils/format_currency';
import { ROUTES } from 'routes/constants';
import { useNavigate, useLocation } from 'react-router-dom';
import useCifaStore from 'stores/cifa/cifa.store';
import { CifaStatusEnum } from 'enum/cifaStatus.enum';

interface Produto {
  id: string;
  nomeRazao: string;
  documento: string;
  valor: string;
  valorNumerico: number;
}

interface SecaoAquisicao {
  titulo: string;
  produtosCnpj: Produto[];
  produtosCpf: Produto[];
}

interface Step2Props {
  answers: {
    secoes: SecaoAquisicao[];
    institution_uuid: string;
  };
  onBack: () => void;
  onSaveSuccess: () => void;
}

const Step2: React.FC<Step2Props> = ({ answers, onBack, onSaveSuccess }) => {
  const { cifa, setPartialForm } = useCifaStore();
  const [localSecoes, setLocalSecoes] = useState(answers.secoes);
  const [produtoEditando, setProdutoEditando] = useState<Produto | null>(null);
  const [secaoEditando, setSecaoEditando] = useState<number | null>(null);
  const [tipoEditando, setTipoEditando] = useState<'cnpj' | 'cpf' | null>(null);
  const [openConfirmacao, setOpenConfirmacao] = useState(false);
  const [produtoExcluir, setProdutoExcluir] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Extrai o ano da rota, ex: /service_management/cifa-2027 => 2027
  const match = /cifa-(\d{4})/.exec(location.pathname);
  const anoReferencia = match ? Number(match[1]) : 2026;

  // Função para gerar key única para seções
  const getSecaoKey = (secao: SecaoAquisicao, index: number) => {
    return secao.titulo.replace(/\s+/g, '-').toLowerCase() || `secao-${index}`;
  };

  useEffect(() => {
    if (!cifa.uuid) {
      navigate(ROUTES[`CIFA${anoReferencia}` as keyof typeof ROUTES] as string);
    }
  }, [cifa.uuid, navigate, anoReferencia]);

  const formatarValorInput = (valor: string) => {
    const apenasDigitos = valor.replace(/\D/g, '');
    if (apenasDigitos === '') return '';
    return getMascaraMoeda(parseInt(apenasDigitos, 10) / 100);
  };

  const { totaisPorSecao, totalGeral } = useMemo(() => {
    const totais = localSecoes.map((secao) => ({
      totalCnpj: secao.produtosCnpj.reduce(
        (sum, p) => sum + p.valorNumerico,
        0,
      ),
      totalCpf: secao.produtosCpf.reduce((sum, p) => sum + p.valorNumerico, 0),
    }));
    return {
      totaisPorSecao: totais,
      totalGeral: totais.reduce(
        (sum, secao) => sum + secao.totalCnpj + secao.totalCpf,
        0,
      ),
    };
  }, [localSecoes]);

  const handleEditar = (
    secaoIndex: number,
    tipo: 'cnpj' | 'cpf',
    produto: Produto,
  ) => {
    setSecaoEditando(secaoIndex);
    setTipoEditando(tipo);
    setProdutoEditando({ ...produto });
  };

  const handleDocumentoChange = (value: string, tipo: 'cnpj' | 'cpf') => {
    if (!produtoEditando) return;

    // Aplica formatação conforme o tipo
    const formattedValue =
      tipo === 'cnpj'
        ? getCNPJFormatado(value.replace(/\D/g, ''))
        : getCPFFormatado(value.replace(/\D/g, ''));

    setProdutoEditando({
      ...produtoEditando,
      documento: formattedValue,
    });
  };

  const getDocumentHelperText = () => {
    if (!tipoEditando || !produtoEditando?.documento) return '';

    if (tipoEditando === 'cnpj' && !validarCNPJ(produtoEditando.documento)) {
      return 'CNPJ inválido';
    }

    if (tipoEditando === 'cpf' && !validarCPF(produtoEditando.documento)) {
      return 'CPF inválido';
    }

    return '';
  };

  const handleSalvarEdicao = () => {
    if (!produtoEditando || secaoEditando === null || !tipoEditando) return;

    if (!produtoEditando.nomeRazao.trim()) {
      setSnackbar({
        open: true,
        message: 'Nome/Razão Social é obrigatório',
        severity: 'error',
      });
      return;
    }

    if (tipoEditando === 'cnpj' && !validarCNPJ(produtoEditando.documento)) {
      setSnackbar({ open: true, message: 'CNPJ inválido', severity: 'error' });
      return;
    }

    if (tipoEditando === 'cpf') {
      const isCpfValid = validarCPF(produtoEditando.documento);
      if (!isCpfValid) {
        setSnackbar({ open: true, message: 'CPF inválido', severity: 'error' });
        return;
      }
    }

    const valorNumerico = getFloat(produtoEditando.valor);
    if (valorNumerico <= 0) {
      setSnackbar({
        open: true,
        message: 'Valor deve ser maior que zero',
        severity: 'error',
      });
      return;
    }

    const updatedSecoes = [...localSecoes];
    const produtoAtualizado = {
      ...produtoEditando,
      valorNumerico,
      valor: getMascaraMoeda(valorNumerico),
    };

    if (tipoEditando === 'cnpj') {
      updatedSecoes[secaoEditando].produtosCnpj = updatedSecoes[
        secaoEditando
      ].produtosCnpj.map((p) =>
        p.id === produtoEditando.id ? produtoAtualizado : p,
      );
    } else {
      updatedSecoes[secaoEditando].produtosCpf = updatedSecoes[
        secaoEditando
      ].produtosCpf.map((p) =>
        p.id === produtoEditando.id ? produtoAtualizado : p,
      );
    }

    setLocalSecoes(updatedSecoes);
    setProdutoEditando(null);
    setSnackbar({
      open: true,
      message: 'Alterações salvas com sucesso',
      severity: 'success',
    });
  };

  const handleExcluir = (id: string) => {
    setProdutoExcluir(id);
    setOpenConfirmacao(true);
  };

  const confirmarExclusao = () => {
    if (!produtoExcluir) return;

    const updatedSecoes = localSecoes.map((secao) => ({
      ...secao,
      produtosCnpj: secao.produtosCnpj.filter((p) => p.id !== produtoExcluir),
      produtosCpf: secao.produtosCpf.filter((p) => p.id !== produtoExcluir),
    }));

    setLocalSecoes(updatedSecoes);
    setOpenConfirmacao(false);
    setSnackbar({
      open: true,
      message: 'Item excluído com sucesso',
      severity: 'success',
    });
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const payload = {
        aquisicoes_pnae_federal_detalhamento: {
          cnpj: localSecoes[0].produtosCnpj.map((p) => ({
            nomeRazao: p.nomeRazao,
            cnpj: p.documento.replace(/\D/g, ''),
            valor: p.valorNumerico,
          })),
          cpf: localSecoes[0].produtosCpf.map((p) => ({
            nomeRazao: p.nomeRazao,
            cpf: p.documento.replace(/\D/g, ''),
            valor: p.valorNumerico,
          })),
        },
        aquisicoes_governo_estado_detalhamento: {
          cnpj: localSecoes[1].produtosCnpj.map((p) => ({
            nomeRazao: p.nomeRazao,
            cnpj: p.documento.replace(/\D/g, ''),
            valor: p.valorNumerico,
          })),
          cpf: localSecoes[1].produtosCpf.map((p) => ({
            nomeRazao: p.nomeRazao,
            cpf: p.documento.replace(/\D/g, ''),
            valor: p.valorNumerico,
          })),
        },
        aquisicoes_prefeitura_detalhamento: {
          cnpj: localSecoes[2].produtosCnpj.map((p) => ({
            nomeRazao: p.nomeRazao,
            cnpj: p.documento.replace(/\D/g, ''),
            valor: p.valorNumerico,
          })),
          cpf: localSecoes[2].produtosCpf.map((p) => ({
            nomeRazao: p.nomeRazao,
            cpf: p.documento.replace(/\D/g, ''),
            valor: p.valorNumerico,
          })),
        },
      };

      // Atualiza o store
      setPartialForm(
        'aquisicoes_pnae_federal_detalhamento',
        payload.aquisicoes_pnae_federal_detalhamento,
      );
      setPartialForm(
        'aquisicoes_governo_estado_detalhamento',
        payload.aquisicoes_governo_estado_detalhamento,
      );
      setPartialForm(
        'aquisicoes_prefeitura_detalhamento',
        payload.aquisicoes_prefeitura_detalhamento,
      );
      setPartialForm('status_aba_4', CifaStatusEnum.APPROVED);

      onSaveSuccess();
    } catch (error: any) {
      console.error('Erro ao salvar:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Erro ao salvar os dados',
        severity: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: '1152px', margin: '0 auto' }}>
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant='h6' sx={{ mb: 2, fontWeight: 'bold' }}>
          Resumo Financeiro
        </Typography>
        <Grid container spacing={2}>
          {localSecoes.map((secao, index) => (
            <Grid item xs={12} md={4} key={getSecaoKey(secao, index)}>
              <Typography variant='subtitle1'>{secao.titulo}</Typography>
              <Typography>
                CNPJ: {formatCurrency(totaisPorSecao[index].totalCnpj)}
              </Typography>
              <Typography>
                CPF: {formatCurrency(totaisPorSecao[index].totalCpf)}
              </Typography>
              <Typography sx={{ fontWeight: 'bold' }}>
                Total:{' '}
                {formatCurrency(
                  totaisPorSecao[index].totalCnpj +
                  totaisPorSecao[index].totalCpf,
                )}
              </Typography>
            </Grid>
          ))}
          <Grid item xs={12}>
            <Typography variant='h6' sx={{ fontWeight: 'bold' }}>
              Total Geral: {formatCurrency(totalGeral)}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {localSecoes.map((secao, secaoIndex) => (
        <Box key={getSecaoKey(secao, secaoIndex)} sx={{ mb: 4 }}>
          <Typography variant='h6' sx={{ fontWeight: 'bold', mb: 2 }}>
            {secao.titulo}
          </Typography>

          {secao.produtosCnpj.length > 0 && (
            <>
              <Typography variant='subtitle1' sx={{ mb: 1 }}>
                Fornecedores CNPJ
              </Typography>
              <TableContainer component={Paper} sx={{ mb: 3 }}>
                <Table>
                  <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableRow>
                      <TableCell>
                        <strong>Razão Social</strong>
                      </TableCell>
                      <TableCell>
                        <strong>CNPJ</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Valor</strong>
                      </TableCell>
                      <TableCell align='right'>
                        <strong>Ações</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {secao.produtosCnpj.map((produto) => (
                      <TableRow key={produto.id}>
                        <TableCell>{produto.nomeRazao}</TableCell>
                        <TableCell>{produto.documento}</TableCell>
                        <TableCell>{produto.valor}</TableCell>
                        <TableCell align='right'>
                          <IconButton
                            onClick={() =>
                              handleEditar(secaoIndex, 'cnpj', produto)
                            }
                          >
                            <EditIcon color='primary' />
                          </IconButton>
                          <IconButton onClick={() => handleExcluir(produto.id)}>
                            <DeleteIcon color='error' />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {secao.produtosCpf.length > 0 && (
            <>
              <Typography variant='subtitle1' sx={{ mb: 1 }}>
                Fornecedores CPF
              </Typography>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableRow>
                      <TableCell>
                        <strong>Razão Social</strong>
                      </TableCell>
                      <TableCell>
                        <strong>CPF</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Valor</strong>
                      </TableCell>
                      <TableCell align='right'>
                        <strong>Ações</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {secao.produtosCpf.map((produto) => (
                      <TableRow key={produto.id}>
                        <TableCell>{produto.nomeRazao}</TableCell>
                        <TableCell>{produto.documento}</TableCell>
                        <TableCell>{produto.valor}</TableCell>
                        <TableCell align='right'>
                          <IconButton
                            onClick={() =>
                              handleEditar(secaoIndex, 'cpf', produto)
                            }
                          >
                            <EditIcon color='primary' />
                          </IconButton>
                          <IconButton onClick={() => handleExcluir(produto.id)}>
                            <DeleteIcon color='error' />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </Box>
      ))}

      <Dialog
        open={!!produtoEditando}
        onClose={() => setProdutoEditando(null)}
        fullWidth
        maxWidth='sm'
      >
        <DialogTitle>Editar Fornecedor</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <TextField
            label='Nome/Razão Social'
            fullWidth
            value={produtoEditando?.nomeRazao ?? ''}
            onChange={(e) =>
              setProdutoEditando({
                ...produtoEditando!,
                nomeRazao: e.target.value,
              })
            }
            sx={{ mb: 3 }}
          />

          <TextField
            label={tipoEditando === 'cnpj' ? 'CNPJ' : 'CPF'}
            fullWidth
            value={produtoEditando?.documento ?? ''}
            onChange={(e) =>
              handleDocumentoChange(e.target.value, tipoEditando!)
            }
            inputProps={{
              maxLength: tipoEditando === 'cnpj' ? 18 : 14,
            }}
            sx={{ mb: 3 }}
            error={
              (tipoEditando === 'cnpj' &&
                !validarCNPJ(produtoEditando?.documento ?? '')) ||
              (tipoEditando === 'cpf' &&
                !validarCPF(produtoEditando?.documento ?? ''))
            }
            helperText={getDocumentHelperText()}
          />

          <TextField
            label='Valor'
            fullWidth
            value={produtoEditando?.valor ?? ''}
            onChange={(e) =>
              setProdutoEditando({
                ...produtoEditando!,
                valor: formatarValorInput(e.target.value),
              })
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProdutoEditando(null)}>Cancelar</Button>
          <Button
            variant='contained'
            onClick={handleSalvarEdicao}
            disabled={
              !produtoEditando?.nomeRazao ||
              (tipoEditando === 'cnpj' &&
                !validarCNPJ(produtoEditando?.documento || '')) ||
              (tipoEditando === 'cpf' &&
                !validarCPF(produtoEditando?.documento || '')) ||
              !produtoEditando?.valorNumerico ||
              produtoEditando?.valorNumerico <= 0
            }
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openConfirmacao} onClose={() => setOpenConfirmacao(false)}>
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography>Tem certeza que deseja excluir este item?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirmacao(false)}>Cancelar</Button>
          <Button variant='contained' color='error' onClick={confirmarExclusao}>
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Stack
        direction='row'
        spacing={2}
        justifyContent='flex-end'
        sx={{ mt: 3 }}
      >
        <Button
          variant='outlined'
          onClick={onBack}
          sx={{ minWidth: '120px' }}
          disabled={isSubmitting}
        >
          Voltar
        </Button>
        <Button
          variant='contained'
          onClick={handleSubmit}
          disabled={isSubmitting}
          sx={{ minWidth: '120px' }}
        >
          {isSubmitting ? (
            <CircularProgress size={24} color='inherit' />
          ) : (
            'Salvar'
          )}
        </Button>
      </Stack>
    </Box>
  );
};

export default Step2;
